// frontend/src/components/ProjectView/ProjectViewPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore'; // Added addDoc, serverTimestamp, where
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
// Legacy component - kept for reference
// import ProductDescriptionSection from './ProductDescriptionSection';

// New TypeScript component from product module
import { ProductDescriptionSection } from '@modules/product/components';
import { NotificationProvider } from '@modules/product/contexts';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { Timestamp } from 'firebase/firestore';
import {
  generateTasksFromDescription,
} from '@modules/product/services/apiService';
import { interviewPayloadBuilder } from '@modules/actors';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { getProviderForModel } from '@modules/ui-components';
import {
  AvailableModelSelector,
  PROJECT_DEFAULT_AI_MODEL,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';

// Client-side task converter (firebase-admin converters are not compatible with client SDK)
const taskConverter = {
  toFirestore(data) {
    const result = { ...data };
    if (result.createdAt instanceof Date) {
      result.createdAt = Timestamp.fromDate(result.createdAt);
    }
    if (result.updatedAt instanceof Date) {
      result.updatedAt = Timestamp.fromDate(result.updatedAt);
    }
    return result;
  },
  fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    const result = { ...data };
    if (result.createdAt?.toDate) {
      result.createdAt = result.createdAt.toDate();
    }
    if (result.updatedAt?.toDate) {
      result.updatedAt = result.updatedAt.toDate();
    }
    return result;
  }
};
import AudienceManagementSection from './AudienceManagementSection';
import ActorManagementSection from './ActorManagementSection';
import ActorMotivationProfilePanel from './ActorMotivationProfilePanel';
import { 
  getProjectActorPath, 
  getProjectAudienceActorsPath,
  getProjectAudiencesPath,
  getProjectBigNumbersPath,
  getProjectPath,
  getProjectRatingsPath,
  getProjectReviewsPath,
  getProjectStudyCaseOutputPath,
  getProjectStudyCasesPath,
  getProjectTasksPath
} from '../../utils/collectionPaths';
// import './ProjectViewPage.css'; // Removido se os estilos MUI são suficientes

// MUI Imports
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person'; // Ícone para Actors
import DescriptionIcon from '@mui/icons-material/Description';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FileCopyIcon from '@mui/icons-material/FileCopy'; // Added for global copy button
import Alert from '@mui/material/Alert';
// For Select All
// For Select All
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'; // For Global Delete button
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // For Export JSON button
// Add new icons if needed for status, though sidebar handles this primarily
// For Kill All button
import AddIcon from '@mui/icons-material/Add'; // Added AddIcon
import ExtensionIcon from '@mui/icons-material/Extension'; // Added for Strategy Blocks
import Dialog from '@mui/material/Dialog'; // Added Dialog
import DialogActions from '@mui/material/DialogActions'; // Added DialogActions
import DialogContent from '@mui/material/DialogContent'; // Added DialogContent
import DialogTitle from '@mui/material/DialogTitle'; // Added DialogTitle
import TextField from '@mui/material/TextField'; // Added TextField
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'; // Icon for AI create
import MenuItem from '@mui/material/MenuItem';
// Added FormControl
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
// Added RefreshIcon

import {
  createActorWithAIWithProxyImmediate
} from '../../services/apiService';
import { useTaskContext } from '../../contexts/TaskContext';
// Import RichMentionEditor
import { getRawDataForReference as getRawDataFromUtils, parseReferenceString as parseRefStr } from '../../utils/mentionUtils';
import { useNotification } from '../../contexts/NotificationContext';
import { ProofSection } from '@modules/social-proofs';
import { useFooterContext } from '../../contexts/FooterContext';
import CloseIcon from "@mui/icons-material/Close";

// === Sprint 6: New strategy-blocks components ===
import { StrategiesListView } from '@modules/strategy-blocks/components/StrategiesListView';
import { StrategyFormDialog } from '@modules/strategy-blocks/components/StrategyFormDialog';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';

// === New TypeScript CreateActorWithAIDialog (for comparison) ===
import { CreateActorWithAIDialog } from '@modules/actors';
import { InsightsExportService, InsightsListView, InsightsDetailDialog } from '@modules/insights-analyzer';
import PsychologyIcon from '@mui/icons-material/Psychology';


// Helper function to convert Tiptap HTML to plain text
function htmlToPlainText(html) {
  if (!html) return '';
  // Create a dummy DOM element
  const div = document.createElement('div');
  div.innerHTML = html;
  // Replace mention chips with their @mention id
  div.querySelectorAll('span[data-mention-id]').forEach(span => {
    const mentionId = span.getAttribute('data-mention-id');
    if (mentionId) {
      span.replaceWith(mentionId);
    }
  });
  // Get the text content (with @mentions in place)
  return div.textContent || '';
}

// Helper function to convert array to comma-separated string (Needed for form)
const arrayToCsv = (arr) => arr && Array.isArray(arr) ? arr.join(', ') : '';

// Helper function to convert comma-separated string to array (Needed for form)
const csvToArray = (csv) => csv ? csv.split(',').map(item => item.trim()).filter(item => item) : [];

// Stable empty array — passing `actorsByAudience[id] || []` would create a new ref each render.
const EMPTY_ACTORS = Object.freeze([]);

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`project-tabpanel-${index}`} aria-labelledby={`project-tab-${index}`} {...other}>
      {value === index && ( <Box sx={{ pt: 3 }}>{children}</Box> )}
    </div>
  );
}

// Add this component before the ProjectViewPage component
const ActorFieldTable = ({ 
  field, 
  actorFormData, 
  actorFormTempInput, 
  setActorFormTempInput,
  setActorFormData,
  onActorArrayInputChange, 
  onAddActorArrayItem, 
  onRemoveActorArrayItem 
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRowIdx, setMenuRowIdx] = useState(null);
  const [sortBy, setSortBy] = useState('description');
  const [sortOrder, setSortOrder] = useState('asc');

  const [projectViewContainerWidth, setProjectViewContainerWidth] = useState(0);



  // Sorting logic
  const sortedItems = React.useMemo(() => {
    const arr = [...actorFormData[field]].map((item, index) => ({
      ...item,
      originalIndex: index // Preserve the original index
    }));
    arr.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [actorFormData[field], sortBy, sortOrder]);

  // Handlers
  const handleMenuOpen = (event, originalIndex) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRowIdx(originalIndex);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRowIdx(null);
  };

  const handleEditRow = (originalIndex) => {
    setEditingIdx(originalIndex);
    // Get the item being edited
    const itemToEdit = actorFormData[field][originalIndex];
    // Set all fields in the temp input
    setActorFormTempInput(prev => ({
      ...prev,
      [field]: {
        description: itemToEdit.description || '',
        fullPhraseFromInterview: itemToEdit.fullPhraseFromInterview || '',
        weight: itemToEdit.weight || 1,
        order: itemToEdit.order || 0
      }
    }));
    handleMenuClose();
    setAddDialogOpen(true); // Open the dialog after setting up the form
  };

  const handleDeleteRow = (originalIndex) => {
    onRemoveActorArrayItem(field, originalIndex);
    handleMenuClose();
  };

  const handleSortCol = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('asc'); }
  };

  const handleAddOrSave = () => {
    if (editingIdx !== null) {
      // Save edit - update existing item
      const updatedItems = [...actorFormData[field]];
      updatedItems[editingIdx] = {
        description: actorFormTempInput[field].description.trim(),
        fullPhraseFromInterview: actorFormTempInput[field].fullPhraseFromInterview.trim(),
        weight: Number(actorFormTempInput[field].weight) || 1,
        order: actorFormData[field][editingIdx].order
      };
      setActorFormData(prev => ({
        ...prev,
        [field]: updatedItems
      }));
      setEditingIdx(null);
    } else {
      // Add new item
      onAddActorArrayItem(field);
    }
    // Clear form and close dialog
    setActorFormTempInput(prev => ({
      ...prev,
      [field]: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
    }));
    setAddDialogOpen(false);
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setActorFormTempInput(prev => ({
      ...prev,
      [field]: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
    }));
    setAddDialogOpen(false);
  };

  return (
    <Box key={field} sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'capitalize', mb: 0 }}>
          {field.replace(/([A-Z])/g, ' $1').trim()}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add
        </Button>
      </Box>
      <Dialog
        open={addDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          {editingIdx !== null 
            ? `Edit ${field.replace(/([A-Z])/g, ' $1').trim()} Item`
            : `Add to ${field.replace(/([A-Z])/g, ' $1').trim()}`
          }
          <IconButton
            onClick={handleCancelEdit}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{
            padding: '0',
            paddingTop: '16px',
            my: 2,
          }}
        >
          <TextField
            label="Description"
            name={`${field}.description`}
            value={actorFormTempInput[field].description}
            onChange={onActorArrayInputChange}
            size="small"
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Full Phrase from Interview"
            name={`${field}.fullPhraseFromInterview`}
            value={actorFormTempInput[field].fullPhraseFromInterview}
            onChange={onActorArrayInputChange}
            size="small"
            fullWidth
            multiline
            minRows={5}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Weight"
            name={`${field}.weight`}
            type="number"
            value={actorFormTempInput[field].weight}
            onChange={onActorArrayInputChange}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button onClick={handleAddOrSave} variant="contained" color="primary">
            {editingIdx !== null ? 'Save Changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'description'}
                  direction={sortBy === 'description' ? sortOrder : 'asc'}
                  onClick={() => handleSortCol('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'fullPhraseFromInterview'}
                  direction={sortBy === 'fullPhraseFromInterview' ? sortOrder : 'asc'}
                  onClick={() => handleSortCol('fullPhraseFromInterview')}
                >
                  Full Phrase from Interview
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'weight'}
                  direction={sortBy === 'weight' ? sortOrder : 'asc'}
                  onClick={() => handleSortCol('weight')}
                >
                  Weight
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">No items added yet.</TableCell>
              </TableRow>
            ) : (
              sortedItems.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell
                    onClick={() => handleEditRow(item.originalIndex)}
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    {item.fullPhraseFromInterview}
                  </TableCell>
                  <TableCell>{item.weight}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={e => handleMenuOpen(e, item.originalIndex)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchorEl}
                      open={Boolean(menuAnchorEl) && menuRowIdx === item.originalIndex}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ sx: { minWidth: 120 } }}
                    >
                      <MenuItem onClick={() => handleEditRow(item.originalIndex)}>Edit</MenuItem>
                      <MenuItem onClick={() => handleDeleteRow(item.originalIndex)}>Delete</MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

function ProjectViewPage({ sidebarOpen, currentDrawerWidth }) { // Added sidebarOpen and currentDrawerWidth
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  const { showNotification } = useNotification();
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  // Legacy TaskContext — kept for callback registrations (registerImmediateRefreshCallback, etc.)
  // startTask is no longer needed — Zustand collection listener auto-detects tasks
  const { setCurrentProjectId, restoreRunningTasks, setRefreshCallback, registerImmediateRefreshCallback, unregisterImmediateRefreshCallback, registerTaskCompletionCallback, unregisterTaskCompletionCallback } = useTaskContext();
  const startTask = () => {}; // no-op: tasks auto-detected by background-tasks module

  const { setProjectContainerWidthFn } = useFooterContext();
  const [actorsContainerWidth, setActorsContainerWidth] = useState(0);

  // Tab mapping for URL parameters
  const tabMapping = {
    'product': 0,
    'audiences': 1,
    'actors': 2,
    'insides': 3,
    'social-proofs': 4,
    'strategies': 5,
    'blocks': 5 // blocks is a sub-tab within strategies
  };

  // Get initial tab from URL parameter
  const getInitialTabIndex = () => {
    // Spec C (C2, AC-C2-5): an actor deep-link (?actorId=…) lands on the
    // Actors tab regardless of the tab param — the actor lives there.
    if (searchParams.get('actorId')) {
      return tabMapping['actors'];
    }
    const tabParam = searchParams.get('tab');
    if (tabParam && tabMapping.hasOwnProperty(tabParam)) {
      return tabMapping[tabParam];
    }
    return 0; // Default to product tab
  };

  const [activeTabIndex, setActiveTabIndex] = useState(getInitialTabIndex());
  const [project, setProject] = useState(null); // Will now include taskStatus and taskType
  const [isDescriptionSaved, setIsDescriptionSaved] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [actorsByAudience, setActorsByAudience] = useState({});
  const [tasks, setTasks] = useState([]); // Add tasks state
  const [studyCases, setStudyCases] = useState([]); // Add study cases state
  const [reviews, setReviews] = useState([]); // Add reviews state
  const [ratings, setRatings] = useState([]); // Add ratings state
  const [bigNumbers, setBigNumbers] = useState([]); // Add big numbers state
  
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // Add individual loading states for each tab
  const [audiencesLoading, setAudiencesLoading] = useState(false);
  const [actorsLoading, setActorsLoading] = useState(false);

  const [selectedActorsForInterview, setSelectedActorsForInterview] = useState(new Map());
  const [isBatchInterviewing, setIsBatchInterviewing] = useState(false); // Added this state
  const [isCopyingGlobalActors, setIsCopyingGlobalActors] = useState(false); // Added this state
  const [isGlobalTaskRunning, setIsGlobalTaskRunning] = useState(false); // Tracks if any major async task is running

  // State for the single Actor Form Dialog
  const [openActorFormDialog, setOpenActorFormDialog] = useState(false);
  const [actorFormData, setActorFormData] = useState({
    name: '', description: '', weight: 1, coreJobs: [], contexts: [], fears: [],
    bigJobs: [], subtasks: [], competitors: [], habits: []
  });
  const [actorFormEditingId, setActorFormEditingId] = useState(null); // null for add, actorId for edit
  const [actorFormParentAudienceId, setActorFormParentAudienceId] = useState(null); // Needed for adding
  const [actorFormParentAudienceName, setActorFormParentAudienceName] = useState(''); // Needed for dialog title
  const [isActorFormProcessing, setIsActorFormProcessing] = useState(false); // Loading state for form save
  const [actorFormTempInput, setActorFormTempInput] = useState({
    coreJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    contexts: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    fears: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    bigJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    subtasks: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    competitors: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
    habits: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
  });

  // State for TypeScript AI Actor Dialog
  const [openNewAIActorDialog, setOpenNewAIActorDialog] = useState(false);

  // State for Insights Analysis detail dialog
  const [viewingInsightsAnalysis, setViewingInsightsAnalysis] = useState(null);
  // R6 deep-link: opened from the strategy form's "Review variables" action —
  // lands the insights dialog on the Variables tab with the generation preselected.
  const [reviewVariablesGenerationId, setReviewVariablesGenerationId] = useState(null);

  const [isDeletingGlobalActors, setIsDeletingGlobalActors] = useState(false);

  // State for tracking actor operations
  const [actorOperations, setActorOperations] = useState(new Map()); // Map<actorId, { operation: string, taskId?: string }>
  const [deletingActors, setDeletingActors] = useState(new Set()); // Set of actor IDs being deleted

  // Interview model picker. Dialog opens before conduct-interview is fired.
  const [interviewModel, setInterviewModel] = useState(PROJECT_DEFAULT_AI_MODEL);
  const [interviewModelTouched, setInterviewModelTouched] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);

  useEffect(() => {
    if (!interviewDialogOpen || interviewModelTouched) return;
    setInterviewModel(projectDefaultModel.value);
  }, [interviewDialogOpen, interviewModelTouched, projectDefaultModel.value]);

  // === Sprint 6: State for new StrategyFormDialog ===
  const [strategyFormDialogOpen, setStrategyFormDialogOpen] = useState(false);
  const [strategyFormMode, setStrategyFormMode] = useState('create');
  const [selectedStrategyForEdit, setSelectedStrategyForEdit] = useState(null);
  /**
   * How many strategies this project has, reported by StrategiesListView when
   * its list settles. `null` = not known yet → the create wizard hides its
   * first-run note rather than guessing (W12 Part B, Task B1).
   */
  const [strategyCount, setStrategyCount] = useState(null);
  const strategyCreateButtonRef = useRef(null);


  // === Sprint 6: StrategyService for new components ===
  const strategyService = useMemo(() => {
    if (!projectId) return null;
    return new StrategyService(projectId);
  }, [projectId]);

  // === Sprint 6: Handlers for StrategyFormDialog ===
  const handleOpenCreateStrategyDialog = useCallback(() => {
    console.log('[ProjectViewPage] Opening StrategyFormDialog in CREATE mode');
    setStrategyFormMode('create');
    setSelectedStrategyForEdit(null);
    setStrategyFormDialogOpen(true);
  }, []);

  const handleOpenEditStrategyDialog = useCallback((strategy) => {
    console.log('[ProjectViewPage] Opening StrategyFormDialog in EDIT mode:', strategy.id);
    setStrategyFormMode('edit');
    setSelectedStrategyForEdit(strategy);
    setStrategyFormDialogOpen(true);
  }, []);

  const handleStrategyFormSave = useCallback((savedStrategy) => {
    console.log('[ProjectViewPage] Strategy saved:', savedStrategy.id);
    console.log('[ProjectViewPage] Mode was:', strategyFormMode);
    // Real-time listener will update the list automatically
  }, [strategyFormMode]);

  const handleStrategyFormClose = useCallback(() => {
    console.log('[ProjectViewPage] Strategy form dialog closed');
    setStrategyFormDialogOpen(false);
  }, []);

  const [selectedAudienceIds, setSelectedAudienceIds] = useState(new Set());

  // Helper functions for managing actor operations
  const startActorOperation = useCallback((actorId, operation, taskId = null) => {
    setActorOperations(prev => {
      const newMap = new Map(prev);
      newMap.set(actorId, { operation, taskId, startTime: Date.now() });
      return newMap;
    });
  }, []);

  const endActorOperation = useCallback((actorId) => {
    setActorOperations(prev => {
      const newMap = new Map(prev);
      newMap.delete(actorId);
      return newMap;
    });
  }, []);

  const isActorBusy = useCallback((actorId) => {
    return actorOperations.has(actorId) || deletingActors.has(actorId);
  }, [actorOperations, deletingActors]);

  const getActorOperation = useCallback((actorId) => {
    return actorOperations.get(actorId);
  }, [actorOperations]);

  const startActorDeletion = useCallback((actorIds) => {
    setDeletingActors(prev => {
      const newSet = new Set(prev);
      actorIds.forEach(id => newSet.add(id));
      return newSet;
    });
  }, []);

  const endActorDeletion = useCallback((actorIds) => {
    setDeletingActors(prev => {
      const newSet = new Set(prev);
      actorIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  }, []);

  // Helper to determine if any audience-specific task is running (used by Kill All button logic)
  const isAnyAudienceTaskRunning = useCallback(() => { // useCallback to stabilize dependencies
    return audiences.some(aud => aud.taskStatus === 'running');
  }, [audiences]);

  useEffect(() => {
    // Removed loading state restrictions - buttons are now always available
    // Only disable buttons when actually performing operations, not when tasks are running in background
    setIsGlobalTaskRunning(false);
  }, []);

  // Handle URL parameter changes for tab navigation (e.g., browser back/forward, external links)
  // Note: This should only sync FROM URL TO state, not the other way around
  // handleTabChange handles state -> URL sync
  useEffect(() => {
    const tabParam = searchParams.get('tab');

    // Spec C (C2, AC-C2-5): actor deep-link wins over the tab param — the
    // actor row lives on the Actors tab (scroll/highlight handled below).
    const actorIdParam = searchParams.get('actorId');
    if (actorIdParam) {
      setActiveTabIndex(prevIndex => (tabMapping['actors'] !== prevIndex ? tabMapping['actors'] : prevIndex));
    } else if (tabParam && tabMapping.hasOwnProperty(tabParam)) {
      const newTabIndex = tabMapping[tabParam];
      // Use functional update to avoid stale closure issues
      setActiveTabIndex(prevIndex => {
        // Only update if actually different (prevents unnecessary re-renders)
        return newTabIndex !== prevIndex ? newTabIndex : prevIndex;
      });
    }

    // Handle deep link to specific insight analysis
    const insightId = searchParams.get('insightId');
    if (insightId && tabParam === 'insides') {
      const loadInsight = async () => {
        try {
          const { InsightsFirestoreService } = await import('@modules/insights-analyzer');
          const service = new InsightsFirestoreService(projectId);
          const analysis = await service.get(insightId);
          if (analysis) {
            setViewingInsightsAnalysis(analysis);
            // Clear the insightId param after opening
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('insightId');
            setSearchParams(newParams, { replace: true });
          }
        } catch (err) {
          console.error('[ProjectViewPage] Failed to load insight:', err);
        }
      };
      loadInsight();
    }
  }, [searchParams]); // Removed activeTabIndex - functional update handles comparison

  // Set initial URL parameter if none exists
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) {
      // Set default tab parameter
      setSearchParams({ tab: 'product' });
    }
  }, []);

  // Dedicated function to refresh only actors data
  const refreshActorsData = useCallback(async () => {
    if (!currentUser || !projectId || !isDescriptionSaved) return;
    setActorsLoading(true);
    
    try {
      // Fetch all audiences first
      const audiencesCol = collection(db, getProjectAudiencesPath(projectId));
      const audiencesQuery = query(audiencesCol, orderBy('creationDate', 'desc'));
      const audienceSnapshot = await getDocs(audiencesQuery);
      const fetchedAudiences = audienceSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Fetch actors for each audience with their subcollections
      const newActorsByAudience = {};
      
      for (const aud of fetchedAudiences) {
        const actorsCol = collection(db, getProjectAudienceActorsPath(projectId, aud.id));
        const actorsQuery = query(actorsCol, orderBy('creationDate', 'desc'));
        const actorSnapshot = await getDocs(actorsQuery);
        
        // Fetch actors with their subcollections
        const actorsWithSubcollections = await Promise.all(actorSnapshot.docs.map(async (docSnap) => {
          const actorData = docSnap.data();
          const actorId = docSnap.id;

          // Fetch all subcollections
          const subcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
          const subcollectionData = {};

          for (const subcollectionName of subcollections) {
            const subcollectionRef = collection(db, `projects/${projectId}/audiences/${aud.id}/actors/${actorId}/${subcollectionName}`);
            const subcollectionSnapshot = await getDocs(subcollectionRef);
            subcollectionData[subcollectionName] = subcollectionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }

          return {
            id: actorId,
            ...actorData,
            ...subcollectionData
          };
        }));

        newActorsByAudience[aud.id] = actorsWithSubcollections;
      }
      
      setActorsByAudience(newActorsByAudience);
    } catch (error) {
      console.error('[ERROR] Failed to refresh actors data:', error);
    } finally {
      setActorsLoading(false);
    }
  }, [currentUser, projectId, isDescriptionSaved]);

  const fetchData = useCallback(async (showMainLoading = true) => {
    if (!currentUser || !projectId) { setLoading(false); return; }
    if (showMainLoading) setLoading(true);
    // setPageError(''); // Comentado para não limpar erros automaticamente a cada fetch
    try {
      const projectRef = doc(db, getProjectPath(projectId));
      const projectSnap = await getDoc(projectRef);
      let currentProjectData;

      if (projectSnap.exists()) {
        currentProjectData = { id: projectSnap.id, ...projectSnap.data() };
        setProject(currentProjectData); // project data now includes taskStatus and taskType from Firestore
        
        // ATUALIZADO: Habilita isDescriptionSaved apenas com a descrição preenchida
        if (currentProjectData.description && currentProjectData.description.trim() !== '') {
          setIsDescriptionSaved(true);
        } else {
          setIsDescriptionSaved(false);
        }
      } else {
        setPageError('Project not found.'); 
        setProject(null); 
        setIsDescriptionSaved(false); 
        if (showMainLoading) setLoading(false); 
        return;
      }

      // Existing logic for fetching audiences and actors
      if (isDescriptionSaved || (currentProjectData?.description && currentProjectData?.interviewQuestions)) {
        setAudiencesLoading(true);
        setActorsLoading(true);
        
        // Audiences and actors are now handled by snapshot listeners
        setAudiencesLoading(false);
        setActorsLoading(false);
      } else {
        setAudiences([]);
        setActorsByAudience({});
        setAudiencesLoading(false);
        setActorsLoading(false);
      }

      // Fetch tasks
      const tasksCol = collection(db, getProjectTasksPath(projectId));
      const tasksQuery = query(tasksCol, orderBy('createdAt', 'desc'));
      const tasksSnapshot = await getDocs(tasksQuery);
      const fetchedTasks = tasksSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setTasks(fetchedTasks);

      // Fetch study cases with their outputs
      const studyCasesCol = collection(db, getProjectStudyCasesPath(projectId));
      const studyCasesQuery = query(studyCasesCol, orderBy('createdAt', 'desc'));
      const studyCasesSnapshot = await getDocs(studyCasesQuery);
      const fetchedStudyCases = [];
      
      for (const docSnap of studyCasesSnapshot.docs) {
        const studyCaseData = { id: docSnap.id, ...docSnap.data() };
        
        // Fetch outputs for this study case
        try {
          const outputsCol = collection(db, getProjectStudyCaseOutputPath(projectId, docSnap.id));
          const outputsSnapshot = await getDocs(outputsCol);
          const outputs = outputsSnapshot.docs.map(outputDoc => ({
            id: outputDoc.id,
            ...outputDoc.data()
          }));
          studyCaseData.outputs = outputs;
        } catch (err) {
          console.warn(`Failed to fetch outputs for study case ${docSnap.id}:`, err);
          studyCaseData.outputs = [];
        }
        
        fetchedStudyCases.push(studyCaseData);
      }
      
      setStudyCases(fetchedStudyCases);

      // Fetch social proof data (reviews, ratings, big numbers)
      const reviewsCol = collection(db, getProjectReviewsPath(projectId));
      const reviewsQuery = query(reviewsCol, orderBy('createdAt', 'desc'));
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const fetchedReviews = reviewsSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setReviews(fetchedReviews);

      const ratingsCol = collection(db, getProjectRatingsPath(projectId));
      const ratingsQuery = query(ratingsCol, orderBy('createdAt', 'desc'));
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const fetchedRatings = ratingsSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setRatings(fetchedRatings);

      const bigNumbersCol = collection(db, getProjectBigNumbersPath(projectId));
      const bigNumbersQuery = query(bigNumbersCol, orderBy('createdAt', 'desc'));
      const bigNumbersSnapshot = await getDocs(bigNumbersQuery);
      const fetchedBigNumbers = bigNumbersSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setBigNumbers(fetchedBigNumbers);

    } catch (err) {
      console.error("Error fetching project data/audiences/actors/tasks: ", err);
      setPageError('Failed to load project data. ' + err.message);
      setAudiencesLoading(false);
      setActorsLoading(false);
    }
    if (showMainLoading) setLoading(false);
  }, [currentUser, projectId, isDescriptionSaved]);

  const handleResize = () => {
    setTimeout(()=> {

      const actorEl = document.getElementById("page-details-container");
      if(actorEl){
        setActorsContainerWidth(actorEl.offsetWidth)
        setProjectContainerWidthFn(actorEl.offsetWidth);
      }
    }, 100)
  };

  useEffect(() => {
    if(project) {
      handleResize();
      window.addEventListener("resize", handleResize);
    }
  }, [project]);

  // Set current project ID in task context and restore running tasks
  useEffect(() => {
    if (projectId && currentUser?.uid) {
      setCurrentProjectId(projectId);
      // Restore running tasks for this project and user
      restoreRunningTasks(projectId, currentUser.uid);
    }
  }, [projectId, currentUser?.uid, setCurrentProjectId, restoreRunningTasks]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Register immediate refresh callback with TaskContext
  // Set up Firestore snapshot listeners for audiences and actors.
  // IMPORTANT: actor listeners must be torn down on every new audience snapshot —
  // the previous version returned a cleanup from inside the onSnapshot callback (dead code),
  // so listeners stacked on each fire and produced N×M setState storms.
  useEffect(() => {
    if (!projectId || !isDescriptionSaved) return;

    const audiencesCol = collection(db, getProjectAudiencesPath(projectId));
    const audiencesQuery = query(audiencesCol, orderBy('creationDate', 'desc'));

    let actorUnsubscribers = [];
    const teardownActorListeners = () => {
      actorUnsubscribers.forEach(unsubscribe => unsubscribe());
      actorUnsubscribers = [];
    };

    const unsubscribeAudiences = onSnapshot(audiencesQuery, async (audienceSnapshot) => {
      const fetchedAudiences = audienceSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setAudiences(fetchedAudiences);
      setAudiencesLoading(false);

      // Tear down previous batch of actor listeners before subscribing to the new set —
      // otherwise they stack across audience snapshot fires.
      teardownActorListeners();

      for (const aud of fetchedAudiences) {
        const actorsCol = collection(db, getProjectAudienceActorsPath(projectId, aud.id));
        const actorsQuery = query(actorsCol, orderBy('creationDate', 'desc'));

        const unsubscribeActors = onSnapshot(actorsQuery, async (actorSnapshot) => {
          // Fetch actors with their subcollections
          const actorsWithSubcollections = await Promise.all(actorSnapshot.docs.map(async (docSnap) => {
            const actorData = docSnap.data();
            const actorId = docSnap.id;

            // Fetch all subcollections
            const subcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
            const subcollectionData = {};

            for (const subcollectionName of subcollections) {
              const subcollectionRef = collection(db, `projects/${projectId}/audiences/${aud.id}/actors/${actorId}/${subcollectionName}`);
              const subcollectionSnapshot = await getDocs(subcollectionRef);
              subcollectionData[subcollectionName] = subcollectionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }

            return {
              id: actorId,
              ...actorData,
              ...subcollectionData
            };
          }));

          setActorsByAudience(prev => ({
            ...prev,
            [aud.id]: actorsWithSubcollections
          }));
        }, (error) => {
          console.error(`Error listening to actors for audience ${aud.id}:`, error);
        });

        actorUnsubscribers.push(unsubscribeActors);
      }

      // Clean up selected actors for deleted audiences
      setSelectedActorsForInterview(prevMap => {
        const newMap = new Map(prevMap);
        const validAudienceIds = new Set(fetchedAudiences.map(aud => aud.id));

        for (const [actorId, actorInfo] of newMap.entries()) {
          if (!validAudienceIds.has(actorInfo.firestoreAudienceId)) {
            newMap.delete(actorId);
          }
        }

        return newMap;
      });
    }, (error) => {
      console.error('Error listening to audiences:', error);
      setAudiencesLoading(false);
    });

    return () => {
      teardownActorListeners();
      unsubscribeAudiences();
    };
  }, [projectId, isDescriptionSaved]);

  // Set refresh callback for AI strategy task completion
  useEffect(() => {
    setRefreshCallback(() => fetchData);
  }, [setRefreshCallback, fetchData]);

  // Register immediate refresh callback for interview tasks
  useEffect(() => {
    const callbackKey = `actors-refresh-${projectId}`;
    const actorsRefreshCallback = (taskId, taskData) => {
      // Only refresh if this is an interview task
      if (taskData.action && (
        taskData.action.includes('Conducting') ||
        taskData.action.includes('interview') ||
        taskData.action.includes('Interview')
      )) {
        // Add a small delay to ensure backend has time to update actor documents
        setTimeout(() => {
          refreshActorsData(); // Use dedicated actors refresh function
        }, 2000); // 2 second delay
      } else {
      }
    };

    registerImmediateRefreshCallback(callbackKey, actorsRefreshCallback);
    return () => {
      unregisterImmediateRefreshCallback(callbackKey);
    };
  }, [projectId, refreshActorsData, registerImmediateRefreshCallback, unregisterImmediateRefreshCallback]);

  // Register task completion callback for managing actor operations
  useEffect(() => {
    const callbackKey = `actor-operations-${projectId}`;
    const actorOperationsCallback = (taskId, taskData) => {
      // For interview tasks, end operations for all actors that have interview operations
      if (taskData.action && (
        taskData.action.includes('Conducting') ||
        taskData.action.includes('interview') ||
        taskData.action.includes('Interview')
      )) {
        setActorOperations(prev => {
          const newMap = new Map(prev);
          const foundActors = [];
          
          for (const [actorId, operation] of newMap.entries()) {
            if (operation.operation === 'interview') {
              foundActors.push(actorId);
              newMap.delete(actorId);
            }
          }
          
          if (foundActors.length > 0) {
          }
          
          return newMap;
        });
      }
    };

    registerTaskCompletionCallback(callbackKey, actorOperationsCallback);

    return () => {
      unregisterTaskCompletionCallback(callbackKey);
    };
  }, [projectId, registerTaskCompletionCallback, unregisterTaskCompletionCallback]);

  const clearProjectTaskStatus = async () => {
    if (!currentUser || !projectId) return;
    try {
      const projectRef = doc(db, getProjectPath(projectId));
      await updateDoc(projectRef, { taskStatus: null, taskType: null });
      fetchData(false); // Refresh project data
    } catch (error) {
      console.error("Error clearing project task status:", error);
    }
  };

  // Handlers for the single Actor Form Dialog
  const handleOpenActorFormDialog = (actorToEdit = null, audienceId = null, audienceName = '') => {
    if (actorToEdit) {
      setActorFormEditingId(actorToEdit.id);
      setActorFormData({
        name: actorToEdit.name || '',
        description: actorToEdit.description || '',
        weight: actorToEdit.weight || 1,
        coreJobs: actorToEdit.coreJobs || [],
        contexts: actorToEdit.contexts || [],
        fears: actorToEdit.fears || [],
        bigJobs: actorToEdit.bigJobs || [],
        subtasks: actorToEdit.subtasks || [],
        competitors: actorToEdit.competitors || [],
        habits: actorToEdit.habits || [],
      });
      setActorFormTempInput({
        coreJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        contexts: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        fears: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        bigJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        subtasks: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        competitors: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        habits: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
      });
      setActorFormParentAudienceId(audienceId);
      setActorFormParentAudienceName(audienceName);
    } else {
      setActorFormEditingId(null);
      setActorFormData({
        name: '', description: '', weight: 1, coreJobs: [], contexts: [], fears: [], bigJobs: [], subtasks: [], competitors: [], habits: []
      });
      setActorFormTempInput({
        coreJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        contexts: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        fears: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        bigJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        subtasks: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        competitors: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
        habits: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
      });
      setActorFormParentAudienceId(audienceId);
      setActorFormParentAudienceName(audienceName);
    }
    setPageError('');
    setOpenActorFormDialog(true);
  };

  const handleCloseActorFormDialog = () => {
    setOpenActorFormDialog(false);
    setActorFormEditingId(null);
    setActorFormData({
      name: '', description: '', weight: 1, coreJobs: [], contexts: [], fears: [], bigJobs: [], subtasks: [], competitors: [], habits: []
    });
    setActorFormTempInput({
      coreJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      contexts: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      fears: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      bigJobs: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      subtasks: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      competitors: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 },
      habits: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
    });
    setActorFormParentAudienceId(null);
    setActorFormParentAudienceName('');
    // Do NOT clear pageError here, only form-specific errors
  };

  const handleActorArrayInputChange = (e) => {
    const { name, value } = e.target;
    const [field, subfield] = name.split('.');
    setActorFormTempInput(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subfield]: value
      }
    }));
  };

  const handleAddActorArrayItem = (field) => {
    if (!actorFormTempInput[field].description.trim()) return;
    setActorFormData(prev => ({
      ...prev,
      [field]: [
        ...prev[field],
        {
          description: actorFormTempInput[field].description.trim(),
          fullPhraseFromInterview: actorFormTempInput[field].fullPhraseFromInterview.trim(),
          weight: Number(actorFormTempInput[field].weight) || 1,
          order: prev[field].length
        }
      ]
    }));
    setActorFormTempInput(prev => ({
      ...prev,
      [field]: { description: '', fullPhraseFromInterview: '', weight: 1, order: 0 }
    }));
  };

  const handleRemoveActorArrayItem = (field, idx) => {
    setActorFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== idx).map((item, i) => ({
        ...item,
        order: i
      }))
    }));
  };

  const handleSaveActorForm = async () => {
    if (!actorFormData.name.trim()) {
      setPageError("Actor name is required.");
      return;
    }
    if (!actorFormEditingId && !actorFormParentAudienceId) {
      setPageError("Internal error: Parent audience not identified for new actor.");
      return;
    }
    setIsActorFormProcessing(true);
    setPageError('');
    try {
      const audienceId = actorFormParentAudienceId;
      const actorsCollectionPath = getProjectAudienceActorsPath(projectId, audienceId);
      const payloadToSave = {
        name: actorFormData.name.trim(),
        description: actorFormData.description.trim(),
        weight: parseInt(actorFormData.weight) || 1,
      };

      let actorRef;
      if (actorFormEditingId) {
        actorRef = doc(db, getProjectActorPath(projectId, audienceId, actorFormEditingId));
        // Spec C (C2, AC-C2-4): scalar edits go through the managed
        // firebase-core path; subcollection rewrites below stay as-is.
        const { ActorService } = await import('@modules/firebase-core/services/ActorService');
        await new ActorService(projectId, audienceId).update(actorFormEditingId, payloadToSave);
      } else {
        const actorsCol = collection(db, actorsCollectionPath);
        const newActorDoc = await addDoc(actorsCol, {
          ...payloadToSave,
          creationDate: serverTimestamp(),
        });
        actorRef = newActorDoc;
      }

      // Handle subcollections
      const subcollections = [
        { name: 'coreJobs', data: actorFormData.coreJobs || [] },
        { name: 'contexts', data: actorFormData.contexts || [] },
        { name: 'fears', data: actorFormData.fears || [] },
        { name: 'bigJobs', data: actorFormData.bigJobs || [] },
        { name: 'subtasks', data: actorFormData.subtasks || [] },
        { name: 'competitors', data: actorFormData.competitors || [] },
        { name: 'habits', data: actorFormData.habits || [] }
      ];

      for (const { name, data } of subcollections) {
        const subcollectionRef = collection(actorRef, name);
        
        // Delete existing documents in the subcollection
        const existingDocs = await getDocs(subcollectionRef);
        const deletePromises = existingDocs.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // Add new documents
        const addPromises = data.map((item, index) => {
          const docData = {
            description: item.description || '',
            fullPhraseFromInterview: item.fullPhraseFromInterview || '',
            weight: Number(item.weight) || 1,
            order: index
          };
          return addDoc(subcollectionRef, docData);
        });
        await Promise.all(addPromises);
      }

      handleCloseActorFormDialog();
      await fetchData();
      // showNotification(actorFormEditingId ? 'Actor updated successfully.' : 'Actor created successfully.');
    } catch (error) {
      console.error('Error saving actor:', error);
      setPageError(`Error saving actor: ${error.message}`);
    } finally {
      setIsActorFormProcessing(false);
    }
  };

  // Memoized — passed as a prop into AudienceManagementSection's useEffect deps,
  // so an unstable identity would re-subscribe its Firestore listener on every render.
  const handleDescriptionAndSettingsSaved = useCallback(() => {
    setIsDescriptionSaved(true);
    fetchData(false);
  }, [fetchData]);

  const handleAudiencesGeneratedOrChanged = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  // ============================================================================
  // Task CRUD callbacks for new TypeScript ProductDescriptionSection
  // ============================================================================

  const taskService = useMemo(() => {
    if (!projectId) return null;
    return new TaskService(projectId, taskConverter);
  }, [projectId]);

  const handleCreateTask = useCallback(async (taskData) => {
    if (!taskService) return;
    try {
      await taskService.create(taskData);
      await fetchData(false);
    } catch (error) {
      console.error('Error creating task:', error);
      setPageError(`Failed to create task: ${error.message}`);
    }
  }, [taskService, fetchData]);

  const handleUpdateTask = useCallback(async (taskId, taskData) => {
    if (!taskService) return;
    try {
      await taskService.update(taskId, taskData);
      await fetchData(false);
    } catch (error) {
      console.error('Error updating task:', error);
      setPageError(`Failed to update task: ${error.message}`);
    }
  }, [taskService, fetchData]);

  const handleDeleteTask = useCallback(async (taskId) => {
    if (!taskService) return;
    try {
      await taskService.delete(taskId);
      await fetchData(false);
    } catch (error) {
      console.error('Error deleting task:', error);
      setPageError(`Failed to delete task: ${error.message}`);
    }
  }, [taskService, fetchData]);

  const handleDeleteTasks = useCallback(async (selectedTaskIds) => {
    if (!taskService || !selectedTaskIds.length) return;
    try {
      await Promise.all(selectedTaskIds.map(id => taskService.delete(id)));
      await fetchData(false);
    } catch (error) {
      console.error('Error deleting tasks:', error);
      setPageError(`Failed to delete tasks: ${error.message}`);
    }
  }, [taskService, fetchData]);

  const handleDuplicateTask = useCallback(async (task) => {
    if (!taskService) return;
    try {
      const { id, createdAt, updatedAt, ...taskData } = task;
      await taskService.create({
        ...taskData,
        name: `${task.name} (copy)`,
      });
      await fetchData(false);
    } catch (error) {
      console.error('Error duplicating task:', error);
      setPageError(`Failed to duplicate task: ${error.message}`);
    }
  }, [taskService, fetchData]);

  // Handle Create Audiences - generates audiences for selected tasks
  // useDeepResearch flag enables Gemini Deep Research mode (3-phase AI pipeline)
  const handleCreateAudiences = useCallback(async (audienceCount, selectedTaskIds, useDeepResearch, model) => {
    if (!isDescriptionSaved) {
      setPageError('Please save the description first using the "Save" button.');
      return;
    }
    if (!selectedTaskIds || selectedTaskIds.length === 0) {
      setPageError('Please select at least one task to generate audiences for.');
      return;
    }

    setPageError('');
    const projectRef = doc(db, getProjectPath(projectId));

    try {
      // Update project status to running
      await updateDoc(projectRef, {
        taskStatus: 'running',
        taskType: 'audiences',
        currentTaskId: null,
        currentTaskName: null
      });
      await fetchData(false);

      // Agent-based audience generation (fire-and-forget)
      const { generateAudiencesViaAgent } = await import('@modules/product/services/apiService');
      const { taskId } = await generateAudiencesViaAgent(
        projectId,
        currentUser.uid,
        selectedTaskIds,
        Number(audienceCount) || 4,
        {
          useDeepResearch: !!useDeepResearch,
          provider: model ? getProviderForModel(model) : undefined,
          model,
        }
      );
      console.log(`[ProjectViewPage] Audience generation started: taskId=${taskId}, useDeepResearch=${!!useDeepResearch}, model=${model || 'default'}`);

      // Show task in UI via TaskContext
      startTask(`Generating ${audienceCount} audiences${useDeepResearch ? ' (Deep Research)' : ''}`);

      // Task runs in background — progress tracked via Firestore listener on backend_tasks/{taskId}
      await updateDoc(projectRef, { taskStatus: 'success', taskType: 'audiences' });
      await fetchData(false);

    } catch (err) {
      console.error('Failed to generate audiences:', err);
      setPageError(`Failed to generate audiences: ${err.message}`);
      await updateDoc(projectRef, { taskStatus: 'error', taskType: 'audiences' });
      await fetchData(false);
    }
  }, [isDescriptionSaved, currentUser, projectId, fetchData, startTask]);

  // Handle Create Tasks from Description - generates tasks from product description
  const handleCreateTasksFromDescription = useCallback(async () => {
    if (!project?.description) {
      setPageError('Please enter a product description first.');
      return;
    }

    setPageError('');
    const projectRef = doc(db, getProjectPath(projectId));

    try {
      await updateDoc(projectRef, {
        taskStatus: 'running',
        taskType: 'tasks'
      });
      await fetchData(false);

      const productData = {
        description: project.description,
        productUrl: project.productUrl || project.url,
        platform: project.platform,
        toneOfVoice: project.toneOfVoice,
        designDescription: project.designDescription,
        previousUserStepDescription: project.previousUserStepDescription,
        category: project.category
      };

      await generateTasksFromDescription(
        productData,
        (progress) => {
          console.log('Task generation progress:', progress);
        },
        { startTask },
        currentUser.uid,
        projectId
      );

      await updateDoc(projectRef, { taskStatus: 'success', taskType: 'tasks' });
      await fetchData(false);

    } catch (err) {
      console.error('Failed to generate tasks:', err);
      setPageError(`Failed to generate tasks: ${err.message}`);
      await updateDoc(projectRef, { taskStatus: 'error', taskType: 'tasks' });
      await fetchData(false);
    }
  }, [project, currentUser, projectId, fetchData, startTask]);

  // Handle Update Interview Questions - adapts questions via AI agent
  const handleUpdateInterviewQuestions = useCallback(async (model) => {
    if (!project?.description) {
      setPageError('Please enter a product description first.');
      return;
    }

    setPageError('');

    try {
      const agentApi = new AgentApiClient();
      const result = await agentApi.adaptQuestions({
        projectId,
        userId: currentUser.uid,
        provider: model ? getProviderForModel(model) : undefined,
        model,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to start questions adaptation');
      }

      console.log('[ProjectViewPage] Adapt questions started, taskId:', result.taskId, 'model:', model || 'default');
      // Task progress tracked via Firestore real-time listener (background-tasks module)

    } catch (err) {
      console.error('Failed to adapt interview questions:', err);
      setPageError(`Failed to adapt interview questions: ${err.message}`);
    }
  }, [project, currentUser, projectId]);

  const handleActorsChangedOrCreated = useCallback(async (createdActorsInfo = null) => {
    await fetchData(false); // Refresh data
    
    // Removed automatic redirect to Actors tab and actor selection logic
    // Users can now manually navigate to the Actors tab when they want to see newly created actors
  }, [fetchData]);

  const handleTabChange = (event, newValue) => {
    if (!isDescriptionSaved && newValue > 0) {
      setPageError("Please use \"Save Description & Settings\" on the first tab to enable other features.");
      return;
    }
    // Additional check for Strategy Blocks tab if it depends on audiences/actors
    // For now, only depends on isDescriptionSaved
    setPageError(''); 
    setActiveTabIndex(newValue);
    
    // Update URL parameter - preserve blocks tab if currently on blocks
    const currentTabParam = searchParams.get('tab');
    const currentStrategyParam = searchParams.get('strategy');
    
    if (newValue === 4 && currentTabParam === 'blocks' && currentStrategyParam) {
      // Preserve blocks tab with strategy parameter
      setSearchParams({ tab: 'blocks', strategy: currentStrategyParam });
    } else {
      // Update URL parameter normally
      const tabNames = Object.keys(tabMapping);
      const tabName = tabNames.find(name => tabMapping[name] === newValue);
      if (tabName) {
        setSearchParams({ tab: tabName });
      }
    }
  };

  const handleToggleActorSelectionForInterview = (actorData, audienceName) => {
    setSelectedActorsForInterview(prevMap => {
      const newMap = new Map(prevMap);
      const parentAudienceObject = audiences.find(aud => aud.name === audienceName);
      if (!parentAudienceObject) {
          console.error(`Could not find audience object for name: ${audienceName} when toggling actor selection.`);
          setPageError(`Internal error: Could not identify parent audience for actor ${actorData.name}.`);
          return prevMap;
      }
      const firestoreAudienceId = parentAudienceObject.id;

      if (newMap.has(actorData.id)) {
        newMap.delete(actorData.id);
      } else {
        newMap.set(actorData.id, { 
            name: actorData.name, 
            description: actorData.description, 
            audienceName: audienceName,
            firestoreActorId: actorData.id, 
            firestoreAudienceId: firestoreAudienceId 
        });
      }
      return newMap;
    });
  };

  // New handler to toggle select all actors within a specific audience
  const handleToggleSelectAllActorsInAudience = (audienceId, actorsInAudience) => {
      setSelectedActorsForInterview(prevMap => {
          const newMap = new Map(prevMap);
          const allInAudienceSelected = actorsInAudience.every(actor => newMap.has(actor.id));

          if (allInAudienceSelected) { // If all are currently selected, deselect them
              actorsInAudience.forEach(actor => newMap.delete(actor.id));
          } else { // If not all are selected, select them
              const parentAudienceObject = audiences.find(aud => aud.id === audienceId);
              if (!parentAudienceObject) {
                  console.error(`Could not find audience object for ID: ${audienceId} when toggling select all actors.`);
                  setPageError(`Internal error: Could not identify parent audience for select all.`);
                  return prevMap;
              }
              actorsInAudience.forEach(actor => {
                  newMap.set(actor.id, {
                      name: actor.name,
                      description: actor.description,
                      audienceName: parentAudienceObject.name,
                      firestoreActorId: actor.id,
                      firestoreAudienceId: audienceId
                  });
              });
          }
          return newMap;
      });
  };


  const handleConductBatchInterview = async () => {
    if (selectedActorsForInterview.size === 0) {
        setPageError("Please select at least one actor to interview.");
        return;
    }

    setPageError('');

    try {
      // Build actors array from selected actors Map
      const actorsPayload = Array.from(selectedActorsForInterview.values()).map(actorInfo => {
        const parentAudience = audiences.find(aud => aud.id === actorInfo.firestoreAudienceId);
        let taskData = {};
        if (parentAudience?.taskId) {
          const fullTask = tasks.find(t => t.id === parentAudience.taskId);
          if (fullTask) {
            taskData = {
              taskId: fullTask.id,
              taskName: fullTask.name || '',
              taskDescription: fullTask.description,
            };
          }
        }

        return {
          actorId: actorInfo.firestoreActorId,
          audienceId: actorInfo.firestoreAudienceId,
          actorName: actorInfo.name,
          actorDescription: actorInfo.description,
          audienceName: actorInfo.audienceName,
          ...taskData,
        };
      });

      const agentApi = new AgentApiClient();
      const result = await agentApi.conductInterview({
        projectId,
        userId: currentUser?.uid,
        actors: actorsPayload,
        provider: getProviderForModel(interviewModel),
        model: interviewModel,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to start interviews');
      }

      console.log('[ProjectViewPage] Interview started, taskId:', result.taskId, 'model:', interviewModel);
      setSelectedActorsForInterview(new Map());
      setInterviewDialogOpen(false);
      // Interview results saved server-side by handler → Firestore real-time listener updates UI

    } catch (err) {
      console.error("Error conducting batch interviews:", err);
      setPageError(`Failed to conduct interviews: ${err.message}`);
    }
  };

  const handleGlobalCopySelectedActors = async () => {
    if (selectedActorsForInterview.size === 0) {
      setPageError("No actors selected to copy.");
      setTimeout(() => setPageError(''), 3000);
      return;
    }
    // Removed loading state - buttons remain available during operation
    setPageError('');

    const dataToCopy = [];
    for (const [, selectedActorInfo] of selectedActorsForInterview) {
      const audienceActors = actorsByAudience[selectedActorInfo.firestoreAudienceId];
      if (audienceActors) {
        const fullActorData = audienceActors.find(actor => actor.id === selectedActorInfo.firestoreActorId);
        if (fullActorData) {
          dataToCopy.push({
            project_title: project?.projectName || "Unknown Project",
            audience: selectedActorInfo.audienceName,
            name: fullActorData.name,
            description: fullActorData.description,
            coreJobs: fullActorData.coreJobs?.map(item => item.description) || [],
            contexts: fullActorData.contexts?.map(item => item.description) || [],
            fears: fullActorData.fears?.map(item => item.description) || [],
            bigJobs: fullActorData.bigJobs?.map(item => item.description) || [],
            subtasks: fullActorData.subtasks?.map(item => item.description) || [],
            competitors: fullActorData.competitors?.map(item => item.description) || [],
            habits: fullActorData.habits?.map(item => item.description) || [],
          });
        } else {
          console.warn(`Could not find full data for actor ID ${selectedActorInfo.firestoreActorId} in audience ID ${selectedActorInfo.firestoreAudienceId}`);
        }
      } else {
        console.warn(`Could not find audience data for ID ${selectedActorInfo.firestoreAudienceId}`);
      }
    }

    if (dataToCopy.length === 0 && selectedActorsForInterview.size > 0) {
        setPageError("Could not retrieve data for any of the selected actors. Check console.");
        setTimeout(() => setPageError(''), 5000);
        return;
    }
    
    if (dataToCopy.length !== selectedActorsForInterview.size) {
        setPageError("Copied data for some actors might be missing or couldn't be retrieved. Check console.");
    }


    const jsonString = JSON.stringify(dataToCopy, null, 2);

    try {
      await navigator.clipboard.writeText(jsonString);
      // showNotification('Successfully copied selected actors to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setPageError('Failed to copy selected actors. See console or try again.');
    }
    // Removed loading state reset - buttons remain available
  };

  const handleExportSelectedActorsJson = async () => {
    if (selectedActorsForInterview.size === 0) {
      setPageError("No actors selected to export.");
      setTimeout(() => setPageError(''), 3000);
      return;
    }

    try {
      const actorSelections = [];
      for (const [, info] of selectedActorsForInterview) {
        actorSelections.push({
          actorId: info.firestoreActorId,
          audienceId: info.firestoreAudienceId,
        });
      }

      const exportService = new InsightsExportService();
      const envelope = await exportService.exportSelectedActors(
        projectId,
        project?.projectName || 'Unknown Project',
        actorSelections,
      );

      const actorCount = envelope.metadata.actorCount;
      const itemCount = envelope.metadata.totalInsightItems;
      InsightsExportService.download(
        envelope,
        `insights_actors_${actorCount}a_${itemCount}i_${Date.now()}.json`,
      );
    } catch (err) {
      console.error('Failed to export actors:', err);
      setPageError('Failed to export actors. See console.');
      setTimeout(() => setPageError(''), 5000);
    }
  };

  const getAllActorsCount = () => {
    let count = 0;
    Object.values(actorsByAudience).forEach(actorList => {
      count += actorList.length;
    });
    return count;
  };

  const areAllActorsSelected = () => {
    const totalActors = getAllActorsCount();
    if (totalActors === 0) return false; // Nothing to select
    return selectedActorsForInterview.size === totalActors;
  };

  const handleToggleSelectAllActors = () => {
    const newMap = new Map();
    if (!areAllActorsSelected()) { // If not all are selected, select all
      audiences.forEach(audience => {
        const actorsInCurrentAudience = actorsByAudience[audience.id] || [];
        actorsInCurrentAudience.forEach(actor => {
          newMap.set(actor.id, {
            name: actor.name,
            description: actor.description,
            audienceName: audience.name,
            firestoreActorId: actor.id,
            firestoreAudienceId: audience.id
          });
        });
      });
    }
    // If all are already selected, newMap remains empty, effectively deselecting all
    setSelectedActorsForInterview(newMap);
  };

  const handleDeleteSelectedActors = async () => {
    if (selectedActorsForInterview.size === 0) {
      setPageError("No actors selected to delete.");
      setTimeout(() => setPageError(''), 3000);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedActorsForInterview.size} selected actor(s) and all their data? This action cannot be undone.`)) {
      return;
    }

    // Start deletion tracking for all selected actors
    const selectedActorIds = Array.from(selectedActorsForInterview.keys());
    startActorDeletion(selectedActorIds);

    // Removed loading state - buttons remain available during operation
    setPageError('');
    let deletedCount = 0;
    const errors = [];

    for (const [actorId, actorInfo] of selectedActorsForInterview) {
      try {
        // Define the subcollections that actors have
        const subcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
        
        // Delete each subcollection
        for (const subcollectionName of subcollections) {
          const subcollectionRef = collection(db, `projects/${projectId}/audiences/${actorInfo.firestoreAudienceId}/actors/${actorId}/${subcollectionName}`);
          const subcollectionSnapshot = await getDocs(subcollectionRef);
          
          // Delete all documents in the subcollection
          for (const subDoc of subcollectionSnapshot.docs) {
            await deleteDoc(subDoc.ref);
          }
        }
        
        // Delete the actor document itself
        const actorRef = doc(db, getProjectActorPath(projectId, actorInfo.firestoreAudienceId, actorId));
        await deleteDoc(actorRef);
        deletedCount++;
      } catch (err) {
        console.error(`Failed to delete actor ${actorInfo.name} (ID: ${actorId}):`, err);
        errors.push(`Failed to delete actor ${actorInfo.name}: ${err.message}`);
      }
    }

    setSelectedActorsForInterview(new Map()); // Clear selection
    await fetchData(false); // Refresh data

    // End deletion tracking for all selected actors
    endActorDeletion(selectedActorIds);

    if (errors.length > 0) {
      setPageError(`Successfully deleted ${deletedCount} actor(s). Errors encountered: ${errors.join('; ')}`);
    } else {
      showNotification(`Successfully deleted ${deletedCount} actor(s) and all their data.`);
    }
    // Removed loading state reset - buttons remain available
  };

  // Legacy AI Actor Creation handlers have been removed - now using TypeScript CreateActorWithAIDialog
   
  const LEGACY_handleAICreateActor_DELETED = async () => {
    // This is dead code - left for reference, never called. Remove in next cleanup.
    if (false) {
      void 0; // Unreachable
      return;
    }
    const selectedAudienceId = aiActorFormData.audienceId;
    if (!selectedAudienceId) {
      setPageError("Please select an audience.");
      return;
    }
    const htmlPrompt = aiActorFormData.prompt; // HTML content from RichMentionEditor
    if (!htmlPrompt.trim()) {
      setPageError("AI prompt cannot be empty.");
      return;
    }

    // Convert HTML prompt to plain text first
    const plainTextPrompt = htmlToPlainText(htmlPrompt);
    if (!plainTextPrompt.trim()) {
        setPageError("AI prompt cannot be empty after HTML processing.");
      return;
    }

    // Removed loading state - buttons remain available during operation
    setPageError('');

    try {
      const selectedAudience = audiences.find(aud => aud.id === selectedAudienceId);
      if (!selectedAudience) {
        throw new Error("Selected audience not found. Please refresh and try again.");
      }
      const audienceName = selectedAudience.name;
      const audienceDescription = selectedAudience.description || ''; // Assuming audience has a description field

      // Build complete source data by fetching actual data from IDs



      
      const completeSourceData = {
        audiences: [],
        tasks: [],
        studyCases: [],
        product: null
      };
      
      // Fetch audience data for each audience ID, but only include selected actors

      if (aiActorFormData.sourceData.audiences && aiActorFormData.sourceData.audiences.length > 0) {
        for (const audienceData of aiActorFormData.sourceData.audiences) {

          const audience = audiences.find(aud => aud.id === audienceData.audienceId);

          if (audience) {
            // Get only the selected actors for this audience
            const allActorsInAudience = actorsByAudience[audienceData.audienceId] || [];
            const selectedActors = allActorsInAudience.filter(actor => 
              audienceData.actorIds.includes(actor.id)
            );
            
            // Remove fullInterviewResult field from each actor to avoid duplication
            const actorsWithoutFullInterviewResult = selectedActors.map(actor => {
              const { fullInterviewResult, ...actorWithoutFullInterviewResult } = actor;
              return actorWithoutFullInterviewResult;
            });
            
            const audienceWithSelectedActors = {
              ...audience,
              actors: actorsWithoutFullInterviewResult
            };
            completeSourceData.audiences.push(audienceWithSelectedActors);
            



          }
        }
      }
      
      // Fetch task data for each task ID
      if (aiActorFormData.sourceData.tasks && aiActorFormData.sourceData.tasks.length > 0) {
        for (const taskId of aiActorFormData.sourceData.tasks) {
          const task = tasks.find(t => t.id === taskId);
          if (task) {
            completeSourceData.tasks.push(task);
          }
        }
      }
      
      // Fetch study case data for each study case ID
      if (aiActorFormData.sourceData.studyCases && aiActorFormData.sourceData.studyCases.length > 0) {
        for (const studyCaseId of aiActorFormData.sourceData.studyCases) {
          const studyCase = studyCases.find(sc => sc.id === studyCaseId);
          if (studyCase) {
            // Fetch output documents from the subcollection
            let outputs = [];
            try {
              const outputsRef = collection(db, getProjectStudyCaseOutputPath(projectId, studyCaseId));
              const outputsSnapshot = await getDocs(outputsRef);
              outputs = outputsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
            } catch (err) {
              console.warn(`Failed to fetch outputs for study case ${studyCaseId}:`, err);
            }
            
            // Include the study case with its outputs
            completeSourceData.studyCases.push({
              ...studyCase,
              outputs: outputs
            });
          }
        }
      }
      

      
      // Add project data to the complete source data if available
      if (project) {
        completeSourceData.product = {
          projectName: project.projectName,
          description: project.description,
          toneOfVoice: project.toneOfVoice,
          designDescription: project.designDescription
        };
      }
      
      // Log the complete source data for debugging


      // 1. Extract all reference strings from the PLAIN TEXT prompt
      const mentionRegex = /@\w[\w.-]*\.[\w.-]+(?:\.[\w.-]+)?(?:\[\d+\])?/g;
      const mentionsInPrompt = [];
      let regexMatch;
      // Use plainTextPrompt here for regex matching
      while ((regexMatch = mentionRegex.exec(plainTextPrompt)) !== null) {
        mentionsInPrompt.push({
          original: regexMatch[0],
          index: regexMatch.index,
          length: regexMatch[0].length,
        });
      }
      mentionsInPrompt.sort((a, b) => a.index - b.index);
      


      
      const replacements = [];
      for (const mentionMatch of mentionsInPrompt) {
        const parsedRef = parseRefStr(mentionMatch.original);

        if (parsedRef) {
          const rawData = getRawDataFromUtils(parsedRef, audiences, actorsByAudience, tasks);

          if (rawData !== null && rawData !== undefined) {
            try {
              let masterKeyName = 'unknown_source';
              if (parsedRef.type === 'actor') {
                const aud = audiences.find(a => a.id === parsedRef.audienceId);
                const act = aud && actorsByAudience[aud.id] ? actorsByAudience[aud.id].find(a => a.id === parsedRef.actorId) : null;
                if (act && act.name) {
                  masterKeyName = act.name;
                } else if (aud && aud.name) {
                  masterKeyName = aud.name;
                }
              } else if (parsedRef.type === 'task') {
                const task = tasks.find(t => t.id === parsedRef.taskId);
                if (task && task.name) {
                  masterKeyName = task.name;
                }
              }
              const dataWithMasterKey = { [masterKeyName]: { [parsedRef.fieldName]: rawData } };
              const jsonDataString = JSON.stringify(dataWithMasterKey);
              replacements.push({ ...mentionMatch, newText: jsonDataString });
            } catch (jsonError) {
              console.error(`Error stringifying data for ${mentionMatch.original}:`, jsonError);
              replacements.push({ ...mentionMatch, newText: mentionMatch.original });
            }
          } else {
            console.warn(`No raw data found for ${mentionMatch.original}`);
            replacements.push({ ...mentionMatch, newText: mentionMatch.original });
          }
        } else {
          console.warn(`Could not parse mention: ${mentionMatch.original}`);
          replacements.push({ ...mentionMatch, newText: mentionMatch.original });
        }
      }

      let processedPrompt = "";
      let currentIndex = 0;
      
      if (replacements.length === 0) {
        // No replacements to make, use the plain text as is
        processedPrompt = plainTextPrompt;

      } else {
        for (const rep of replacements) {
          // Use plainTextPrompt here for substring operations
          processedPrompt += plainTextPrompt.substring(currentIndex, rep.index);
          processedPrompt += rep.newText;
          currentIndex = rep.index + rep.length;
        }
        // And here
        processedPrompt += plainTextPrompt.substring(currentIndex);
      }





      
      // Start the task and get payload sent confirmation
      const actorResponse = await createActorWithAIWithProxyImmediate(
        aiActorFormData.name.trim(),
        audienceName,
        audienceDescription,
        selectedAudienceId,
        processedPrompt,
        completeSourceData, // Send the complete fetched data instead of just sourceData with IDs
        (message) => {
          if (showNotification) showNotification(message, 'info');
        },
        { startTask },
        currentUser.uid,
        projectId
      );
      
      // Check if payload was sent successfully
      if (actorResponse && actorResponse.status === 'payload_sent') {
        // Close the dialog immediately
        handleCloseAICreateActorDialog();
        
        // Show notification that task started
        // showNotification('AI Actor creation started! Task monitoring via Firestore listener.', 'info');
      } else {
        throw new Error('Failed to start AI actor creation');
      }

    } catch (err) {
      console.error("Error creating actor with AI:", err);
      setPageError(`Failed to create actor with AI: ${err.message}`);
    }
    // Removed loading state reset - buttons remain available
  };

  // Function to select all new audiences without switching tabs
  const handleAudiencesGeneratedAndSelect = useCallback(async (newAudienceIds) => {
    // First refresh the data to get the new audiences
    await fetchData(false);

    // Then set the selection without switching tabs
    if (newAudienceIds && Array.isArray(newAudienceIds) && newAudienceIds.length > 0) {
      setSelectedAudienceIds(new Set(newAudienceIds));
      // Removed automatic redirect to Audiences tab
      // Users can manually navigate to the Audiences tab when they want to see newly created audiences
    } else {
      // Handle case when no audiences were created
    }
  }, [fetchData, setSelectedAudienceIds]);

  if (loading && !project) return ( 
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 150px)' }}>
      <CircularProgress />
    </Box>
  );
  if (pageError && !project && !loading) return <Alert severity="error" sx={{m:2}}>{pageError}</Alert>; // Mostra erro se o projeto não carregou
  if (!project && !loading && !pageError) return <Alert severity="info" sx={{m:2}}>Project not found or could not be loaded.</Alert>;



  return (
    <Box id='product-view-container' sx={{ px: "24px" }}>
      {pageError && !openActorFormDialog && (
        <Alert 
          severity="error" 
          sx={{my: 2, whiteSpace: 'pre-wrap'}} 
          onClose={() => setPageError('')}
        >
          {pageError}
        </Alert>
      )}
      
      <Box id="page-details-container" sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          aria-label="Project details tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 'auto',
            pt: '1px',
          }}
        >
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Product" icon={<DescriptionIcon />} iconPosition="start" id="project-tab-0" aria-controls="project-tabpanel-0" />
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Audiences" icon={<GroupIcon />} iconPosition="start" id="project-tab-1" aria-controls="project-tabpanel-1" disabled={!isDescriptionSaved} />
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Actors" icon={<PersonIcon />} iconPosition="start" id="project-tab-2" aria-controls="project-tabpanel-2" disabled={!isDescriptionSaved || audiences.length === 0} />
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Insights" icon={<PsychologyIcon />} iconPosition="start" id="project-tab-3" aria-controls="project-tabpanel-3" disabled={!isDescriptionSaved || audiences.length === 0} data-testid="project-tab-insides" />
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Social Proofs" icon={<DescriptionIcon />} iconPosition="start" id="project-tab-4" aria-controls="project-tabpanel-4" disabled={!isDescriptionSaved}/>
          <Tab sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto', }} label="Strategies" icon={<ExtensionIcon />} iconPosition="start" id="project-tab-5" aria-controls="project-tabpanel-5" disabled={!isDescriptionSaved} data-testid="project-tab-strategies" />
        </Tabs>
      </Box>

      <TabPanel value={activeTabIndex} index={0}>
        {project && ( // Garante que 'project' existe antes de renderizar
          <Box sx={{ position: 'relative', overflow: 'hidden', height: 'calc(100vh - 92px)', overflowY: 'auto', }}>
            {/* <Typography variant="body2" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Don't forget to check the "Tasks" tab within this section to manage your product tasks!
            </Typography> */}
            <NotificationProvider>
              <ProductDescriptionSection
                project={project}
                projectId={projectId}
                currentUser={currentUser}
                onDescriptionAndSettingsSaved={handleDescriptionAndSettingsSaved}
                isDescriptionEffectivelySaved={isDescriptionSaved}
                onAudiencesGenerated={handleAudiencesGeneratedOrChanged}
                fetchProjectData={() => fetchData(false)}
                clearProjectTaskStatus={clearProjectTaskStatus}
                // Task props
                tasks={tasks}
                onCreateTask={handleCreateTask}
                onUpdateTask={handleUpdateTask}
                onDeleteTask={handleDeleteTask}
                onDeleteTasks={handleDeleteTasks}
                onDuplicateTask={handleDuplicateTask}
                onCreateAudiences={handleCreateAudiences}
                onCreateTasksFromDescription={handleCreateTasksFromDescription}
                onUpdateInterviewQuestions={handleUpdateInterviewQuestions}
                variables={project?.variableSet?.variables ?? {}}
                currentVariableVersion={project?.variableSet?.currentVersion}
                onVariablesChange={async (variables, reviewed) => {
                  const { VariableEditingService } = await import('@modules/firebase-core/services/VariableEditingService');
                  await new VariableEditingService(db).saveProject(projectId, variables, reviewed);
                  await fetchData(false);
                }}
                onSaveVariableVersion={async (description) => {
                  const { VariableVersionService } = await import('@modules/firebase-core/services/VariableVersionService');
                  await new VariableVersionService(db).createVersion(projectId, project?.variableSet?.variables ?? {}, description, currentUser?.uid);
                  await fetchData(false);
                }}
              />
            </NotificationProvider>
          </Box>
        )}
        {!project && !loading && <Typography>Loading project details...</Typography>} {/* Feedback se project ainda é null */}
      </TabPanel>
      <TabPanel value={activeTabIndex} index={1}>
        {isDescriptionSaved && project && (
          <>
            {audiencesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading audiences...</Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative', overflow: 'hidden', height: 'calc(100vh - 92px)', overflowY: 'auto', }}>
                <AudienceManagementSection
                  project={project} // Pass the whole project object
                  projectId={projectId}
                  audiences={audiences}
                  onAudiencesChange={handleAudiencesGeneratedOrChanged}
                  onActorsCreated={handleActorsChangedOrCreated}
                  currentUser={currentUser}
                  fetchProjectData={() => fetchData(false)} // Pass function to refresh data
                  clearProjectTaskStatus={clearProjectTaskStatus} // Pass function to clear status
                  selectedAudienceIds={selectedAudienceIds}
                  setSelectedAudienceIds={setSelectedAudienceIds}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  tasks={tasks} // Pass tasks to fetch task names
                />
              </Box>
            )}
          </>
        )}
        {!isDescriptionSaved && activeTabIndex === 1 && <Alert severity="info">Please save the product description on the first tab to manage audiences.</Alert>}
      </TabPanel>
      <TabPanel value={activeTabIndex} index={2}>
        {isDescriptionSaved && project && ( // Condição principal
          <Box
            sx={{
              height: 'calc(100vh - 92px)',
              position: 'relative',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: {xs: 1, sm: 2, md: 3},
                mt: 0,
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
              style={{
                height: '100%',
                overflow: 'hidden',
                overflowY: 'auto',
                paddingBottom: '86px',
              }}
            >
              {actorsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading actors...</Typography>
                </Box>
              ) : audiences.length > 0 ? audiences.map(audience => (
                <Box key={audience.name}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> {/* Flex container for title and button */}
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom={false} // Set gutterBottom to false here
                      sx={{
                        display:'flex',
                        alignItems:'center',
                        mb: 0,
                      }}
                    > {/* Adjust margin bottom */}
                      <GroupIcon sx={{mr:1, color: 'action.active'}}/>
                      {audience.name}
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />}
                      onClick={() => handleOpenActorFormDialog(null, audience.id, audience.name)} // Call handler in parent
                      disabled={isGlobalTaskRunning}
                    >
                      Add Actor
                    </Button>
                  </Box>
                  <ActorManagementSection
                    projectId={projectId}
                    audienceId={audience.id}
                    audienceName={audience.name}
                    actors={actorsByAudience[audience.id] || EMPTY_ACTORS}
                    onActorsChange={handleActorsChangedOrCreated}
                    currentUser={currentUser}
                    selectedActorsForInterview={selectedActorsForInterview}
                    onToggleActorSelection={handleToggleActorSelectionForInterview}
                    isGlobalTaskRunning={isGlobalTaskRunning}
                    onEditActor={(actor) => handleOpenActorFormDialog(actor, audience.id, audience.name)}
                    onToggleSelectAllActorsInAudience={(actors) => handleToggleSelectAllActorsInAudience(audience.id, actors)}
                    isActorBusy={isActorBusy}
                    getActorOperation={getActorOperation}
                    startActorDeletion={startActorDeletion}
                    endActorDeletion={endActorDeletion}
                    highlightActorId={searchParams.get('actorId') || undefined}
                  />
                </Box>
              )) : (
                activeTabIndex === 2 && <Alert severity="info">No audiences found. Please generate audiences in the 'Audiences' tab first.</Alert>
              )}

              {isDescriptionSaved && audiences.length > 0 && selectedActorsForInterview.size === 0 && activeTabIndex === 2 && !actorsLoading && (
                <Typography sx={{mt:2, textAlign: 'center', color: 'text.secondary'}}>Select actors from the tables above to conduct interviews.</Typography>
              )}
            </Paper>

            {/* Single Dialog for Add/Edit Actor */}
            <Dialog
              open={openActorFormDialog}
              onClose={handleCloseActorFormDialog}
              maxWidth="lg"
              fullWidth
              fullScreen
              scroll="paper"
              slotProps={{
                backdrop: {
                  sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    backdropFilter: 'blur(20px)',
                  }
                }
              }}
            >
              <DialogTitle
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 0 8px',
                }}
              >
                {actorFormEditingId ? `Edit Actor: ${actorFormData.name}` : `Add New Actor to "${actorFormParentAudienceName}"`}
                <IconButton
                  onClick={handleCloseActorFormDialog}
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.action.hover,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                sx={{
                  padding: '0',
                  my: 2,
                }}
              >
                {pageError && openActorFormDialog && <Alert severity="error" sx={{ mb:1, fontSize: '0.9rem' }}>{pageError}</Alert>} {/* Show page error in dialog */}
                <TextField autoFocus margin="dense" id="actorFormName" label="Actor Name" type="text" fullWidth variant="outlined"
                  value={actorFormData.name}
                  onChange={(e) => setActorFormData({...actorFormData, name: e.target.value})}
                  required sx={{ mb: 2 }}
                />
                <TextField margin="dense" id="actorFormDescription" label="Actor Description" type="text" fullWidth multiline rows={3} variant="outlined"
                  value={actorFormData.description}
                  onChange={(e) => setActorFormData({...actorFormData, description: e.target.value})}
                  sx={{ mb: 2 }}
                />
                <TextField margin="dense" id="actorFormWeight" label="Weight" type="number" fullWidth variant="outlined"
                  value={actorFormData.weight || 1}
                  onChange={(e) => setActorFormData({...actorFormData, weight: e.target.value})}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                />
                {/* <Typography variant="subtitle2" sx={{mt:1, mb:1, color: 'text.secondary'}}>Enter items for each field below:</Typography> */}

                {['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'].map((field) => (
                  <ActorFieldTable
                    key={field}
                    field={field}
                    actorFormData={actorFormData}
                    actorFormTempInput={actorFormTempInput}
                    setActorFormTempInput={setActorFormTempInput}
                    setActorFormData={setActorFormData}
                    onActorArrayInputChange={handleActorArrayInputChange}
                    onAddActorArrayItem={handleAddActorArrayItem}
                    onRemoveActorArrayItem={handleRemoveActorArrayItem}
                  />
                ))}

                {/* Motivation Profile — only for existing actors (needs a saved actorId) */}
                {actorFormEditingId && actorFormParentAudienceId && (
                  <ActorMotivationProfilePanel
                    projectId={projectId}
                    audienceId={actorFormParentAudienceId}
                    actorId={actorFormEditingId}
                    actorName={actorFormData.name}
                  />
                )}
              </DialogContent>
              <DialogActions sx={{ p: '16px 0 0' }}>
                <Button onClick={handleSaveActorForm} variant="contained" disabled={isActorFormProcessing}>
                  {isActorFormProcessing ? <CircularProgress size={18} color="inherit"/> : (actorFormEditingId ? 'Save Changes' : 'Create Actor')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Conduct Interview — model picker dialog. */}
            <Dialog
              open={interviewDialogOpen}
              onClose={() => setInterviewDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Conduct Interview</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Conducting interviews for <strong>{selectedActorsForInterview.size}</strong> selected {selectedActorsForInterview.size === 1 ? 'actor' : 'actors'}. Each interview runs the JTBD simulation and extracts insights into 7 categories.
                  </Typography>
                  <AvailableModelSelector
                    projectId={projectId}
                    value={interviewModel}
                    onChange={(model) => {
                      setInterviewModelTouched(true);
                      setInterviewModel(model);
                    }}
                    label="AI Model"
                    size="small"
                    fullWidth
                    testId="interview-model-selector"
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInterviewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConductBatchInterview}
                  variant="contained"
                  color="secondary"
                  startIcon={<RecordVoiceOverIcon />}
                  disabled={selectedActorsForInterview.size === 0}
                >
                  Start Interview
                </Button>
              </DialogActions>
            </Dialog>

            {/* TypeScript CreateActorWithAIDialog */}
            <CreateActorWithAIDialog
              open={openNewAIActorDialog}
              onClose={() => setOpenNewAIActorDialog(false)}
              projectId={projectId}
              currentUser={currentUser}
              project={project}
              audiences={audiences}
              actorsByAudience={actorsByAudience}
              tasks={tasks}
              studyCases={studyCases}
              showNotification={showNotification}
              onSuccess={() => {
                setOpenNewAIActorDialog(false);
                // Optionally refresh data after actor creation
              }}
            />

            {/*{isDescriptionSaved && audiences.length > 0 && selectedActorsForInterview.size === 0 && activeTabIndex === 2 && !actorsLoading && (*/}
            {/*  <Typography sx={{mt:2, textAlign: 'center', color: 'text.secondary'}}>Select actors from the tables above to conduct interviews.</Typography>*/}
            {/*)}*/}

            {/* Footer */}
            {isDescriptionSaved && audiences.length > 0 && activeTabIndex === 2 && ( // Always render if tab 2 is active and conditions met
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  mt: 2,
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 1.5 },
                  flexWrap: 'wrap',
                  zIndex: 1,
                  background: 'none',
                  boxShadow: 'none',
                  backdropFilter: 'blur(34px)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      setInterviewModelTouched(false);
                      setInterviewModel(projectDefaultModel.value);
                      setInterviewDialogOpen(true);
                    }}
                    disabled={
                      isDeletingGlobalActors ||
                      selectedActorsForInterview.size === 0
                    }
                    startIcon={<RecordVoiceOverIcon />}
                  >
                    {`Conduct Interview`}
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => setOpenNewAIActorDialog(true)}
                    disabled={audiences.length === 0}
                    startIcon={<AutoFixHighIcon />}
                  >
                    Create Actor with AI
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleExportSelectedActorsJson}
                    disabled={isDeletingGlobalActors || selectedActorsForInterview.size === 0}
                    startIcon={<FileDownloadIcon />}
                  >
                    Export JSON
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleGlobalCopySelectedActors}
                    disabled={isDeletingGlobalActors || selectedActorsForInterview.size === 0}
                    startIcon={<FileCopyIcon />}
                  >
                    Copy
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDeleteSelectedActors}
                    disabled={isDeletingGlobalActors || selectedActorsForInterview.size === 0}
                    startIcon={<DeleteSweepIcon />}
                  >
                    {`Delete`}
                  </Button>
                </Box>

                {/* Temporary reset button for stuck interview state */}
                {/* {(isGlobalTaskRunning || project?.taskStatus === 'running' || project?.taskStatus === 'error') && (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={async () => {
                      try {
                        const projectRef = doc(db, getProjectPath(projectId));
                        await updateDoc(projectRef, { taskStatus: null, taskType: null });
                        showNotification('Interview state reset successfully!', 'success');
                        fetchData(false);
                      } catch (error) {
                        console.error('Error resetting interview state:', error);
                        showNotification('Failed to reset interview state', 'error');
                      }
                    }}
                    startIcon={<RefreshIcon />}
                  >
                    Reset Interview State
                  </Button>
                )} */}
              </Paper>
            )}

          </Box>
        )}
        {!isDescriptionSaved && activeTabIndex === 2 && <Alert severity="info">Please save the product description on the first tab to manage actors.</Alert>}
      </TabPanel>
      {/* === Insights Tab === */}
      <TabPanel value={activeTabIndex} index={3}>
        {isDescriptionSaved && audiences.length > 0 && projectId ? (
          <Paper elevation={0} sx={{ p: {xs: 1, sm: 2, md: 3}, mt: 0 }}>
            <InsightsListView
              projectId={projectId}
              userId={currentUser?.uid}
              onView={(analysis) => setViewingInsightsAnalysis(analysis)}
              onTaskStarted={(taskId, description) => startTask(description)}
            />
          </Paper>
        ) : !isDescriptionSaved && activeTabIndex === 3 ? (
          <Alert severity="info">Please save the product description on the first tab to manage insides.</Alert>
        ) : null}
      </TabPanel>
      <TabPanel value={activeTabIndex} index={4}>
        {project && (
          <>
            <ProofSection
              project={project}
              projectId={projectId}
              fetchProjectData={() => fetchData(false)}
            />
          </>
        )}
        {!project && !loading && <Typography>Loading project details...</Typography>} {/* Feedback se project ainda é null */}
      </TabPanel>
      {/* === Sprint 6: Strategies Tab - NEW COMPONENTS === */}
      <TabPanel value={activeTabIndex} index={5}>
        {isDescriptionSaved && project && currentUser && projectId && strategyService ? (
          <Paper elevation={0} sx={{ p: {xs: 1, sm: 2, md: 3}, mt: 0 }}>
            <StrategiesListView
              projectId={projectId}
              strategyService={strategyService}
              onCreate={handleOpenCreateStrategyDialog}
              onEdit={handleOpenEditStrategyDialog}
              onStrategiesLoaded={setStrategyCount}
            />
          </Paper>
        ) : !isDescriptionSaved && activeTabIndex === 5 ? (
          <Alert severity="info">Please save the product description on the first tab to manage strategies.</Alert>
        ) : (
          <Alert severity="error">Failed to initialize strategy service</Alert>
        )}
      </TabPanel>

      {/* === Sprint 6: Strategy Form Dialog === */}
      {strategyService && (
        <StrategyFormDialog
          open={strategyFormDialogOpen}
          mode={strategyFormMode}
          projectId={projectId}
          strategyId={selectedStrategyForEdit?.id}
          initialData={strategyFormMode === 'edit' ? selectedStrategyForEdit ?? undefined : undefined}
          strategyService={strategyService}
          onClose={handleStrategyFormClose}
          onSave={handleStrategyFormSave}
          triggerRef={strategyCreateButtonRef}
          existingStrategyCount={strategyCount ?? undefined}
          onReviewVariables={async ({ analysisId, generationId }) => {
            try {
              const { InsightsFirestoreService } = await import('@modules/insights-analyzer');
              const service = new InsightsFirestoreService(projectId);
              const analysis = await service.get(analysisId);
              if (analysis) {
                setReviewVariablesGenerationId(generationId);
                setViewingInsightsAnalysis(analysis);
              }
            } catch (err) {
              console.error('Failed to open variable review surface', err);
            }
          }}
        />
      )}
      {/* Insights Detail Dialog */}
      <InsightsDetailDialog
        open={Boolean(viewingInsightsAnalysis)}
        analysis={viewingInsightsAnalysis}
        userId={currentUser?.uid}
        onTaskStarted={(taskId, description) => startTask(description)}
        initialTab={reviewVariablesGenerationId ? 'variables' : undefined}
        initialGenerationId={reviewVariablesGenerationId ?? undefined}
        onClose={() => {
          setViewingInsightsAnalysis(null);
          setReviewVariablesGenerationId(null);
        }}
      />
    </Box>
  );
}

export default ProjectViewPage;
